import React, { useCallback, useRef, useState } from "react";
import useBlotter from "./useBlotter";
import {useGptResponses} from "../../../hooks/useGptResponses";
import {useSnapshot} from "../../../components/Snapshot/useSnapshot";
import useTickerMenu from "./useTickerMenu";
import {useSelector, useDispatch} from "react-redux";
import {selectResponseById} from "../../../store/slices/gpt.slice";
import {Col, Row, Modal, Space, Tooltip} from "antd";
import Snapshot from "../../../components/Snapshot/Snapshot";
import StockChart from "../../../charts/StockChart";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import TimeseriesMenu from "./TimeseriesMenu";
import YearlyReturnsTable from "../../../SuperComponents/Beta/YearlyReturnsTable";
import BlotterResponse from "./BlotterResponse";
import {formatDataForStockChart} from "./dataUtils";
import {useRelativeMetrics} from "../../../SuperComponents/StockCharts/useRelativeMetrics";
import { CoPresentRounded, CloseRounded, PlaylistAdd, WaterfallChartRounded } from "@mui/icons-material";
import { getDeltaSeries } from "./dataUtils";
import { rollingSum } from "../../../utils/math";
import Spinner from "../../../components/Spinner";
import ChartSelector from "./ChartSelector";
import { formatTimeseriesForBlotterView, stackSnapshots } from "./blotterUtils";
import {fetchCompositeRelativeMetric} from "../../../api/timeseriesApi";
import {selectCompositeRelativeMetric} from "../../../store/slices/timeseries.slice";


const CloseButton = ({text, toggle})=>{
    return (
        <Button size={'small'}
                variant={'outlined'}
                startIcon={<CloseRounded fontSize={'small'} color={'error'}/>}
                onClick={()=>toggle(false)}
        >
            {text}
        </Button>
    )
}


const PortfolioChart = ({ portfolioTimeSeries, benchmarkTimeSeries, portfolio, categoryMapping, tickerLookup,
                        addTimeseriesSymbols, removeTimeseriesSymbols, extraTimeseries, allTickers }) => {
    const chartRef = useRef(null);
    const dispatch = useDispatch();
    const relativeMetrics = useRelativeMetrics({benchmark: portfolio?.benchmark});
    const [promptRows, setPromptRows] = useState(null);
    const [promptVisible, setPromptVisible] = useState(false);

    const [loadingComposite, setLoadingComposite] = useState(false);

    const [selectedTickers, setSelectedTickers] = useState([]);
    const [showPortfolioTurnover, setShowPortfolioTurnover] = useState(false);
    const [showPortfolioCompositeMetrics, setShowPortfolioCompositeMetrics] = useState(false);
    const [showReturnsTable, setShowReturnsTable] = useState(false);
    const [showChartSelector, setShowChartSelector] = useState(false);
    const [turnoverSeries, setTurnoverSeries] = useState(null);
    const [selectedDates, setSelectedDates] = useState([]);
    const { generatePrompt } = useBlotter();
    const { responseId, submitPrompt, loadingResponse } = useGptResponses();
    const { snapshotData, snapshotCallback, snapshotDate } = useSnapshot();
    const { onPlotHoldings } = useTickerMenu(portfolioTimeSeries, benchmarkTimeSeries, extraTimeseries, addTimeseriesSymbols, removeTimeseriesSymbols);

    // const selectedPoints = useSelector(state => state.appStates.selectedChartPoints);
    const gptResponse = useSelector(state => selectResponseById(state, responseId));
    const compositeMetric = useSelector(state => selectCompositeRelativeMetric(state, portfolio.id, 'beta', 252));

    const handleFetchCompositeMetric = useCallback(()=>{
        if (!compositeMetric || compositeMetric.length === 0) {
            setLoadingComposite(true)
            dispatch(fetchCompositeRelativeMetric({ portfolioId: portfolio.id, metricName: 'beta', window: 252 })).then((resp)=>{
                setShowPortfolioCompositeMetrics(true);
                setLoadingComposite(false);
            })
        }
        else{
            setShowPortfolioCompositeMetrics(true);
        }
    }, [dispatch, portfolio.id, compositeMetric]);


    const handleDeltaSeries = useCallback(()=>{
        if (showPortfolioTurnover===false) {
            const turnoverSeries = getDeltaSeries(portfolioTimeSeries);
            setTurnoverSeries(turnoverSeries);
            setShowPortfolioTurnover(true)
        }
        else {
            setShowPortfolioTurnover(false);
        }
    })


    const dataSeries = formatDataForStockChart(portfolioTimeSeries, benchmarkTimeSeries, extraTimeseries, snapshotCallback, portfolio);


    const submitGptPrompt = (selectedPoints)=>{
        const dates = selectedPoints.map(x=>x.timestamp);
        const snapshots = stackSnapshots(selectedPoints);
        const {promptString, rows} = generatePrompt(snapshots, extraTimeseries && extraTimeseries.map(x=>x.symbol), null);
        submitPrompt(JSON.stringify(promptString), `Activity Insight: ${portfolio.name}`);
        setPromptRows(rows);
        setSelectedDates(dates);

    }

    let chartDataSeriesConfig = [
                        {
                            plot: 0,
                            data: dataSeries,
                            yScale: 'percent',
                            yLabelPostfix: '%',
                            stroke: '#0097fb',
                            legend: {
                                format: "%",
                                precision: 2
                            },
                            height: 400,
                        },

    ];
    if (turnoverSeries && showPortfolioTurnover){
        const sumValues = rollingSum(turnoverSeries.map(x=>x[1]), 5);
        const sumSeries = [...turnoverSeries].slice(5, ).map((x, i)=>[x[0], sumValues[i]]);
        chartDataSeriesConfig.push({
            plot: 1,
            data: [
                {
                    name: 'Daily Change',
                    data: turnoverSeries
                },
                {
                    name: '5 Day Change',
                    data: sumSeries,
                    stroke: '#f36581'
                }
            ],
            height: 200,
            xGrid: false,
            yGrid: false,
        });
    }
    if (compositeMetric && showPortfolioCompositeMetrics) {
        chartDataSeriesConfig.push({
            plot: chartDataSeriesConfig.length,
            data: [
                {
                    name: "Composite Beta",
                    data: compositeMetric.map(x=>[x[0], (x[1]*100).toFixed(1)]),
                },
                {
                    name: `${portfolio.benchmark?.toUpperCase() ?? 'Benchmark'} Benchmark`,
                    data: compositeMetric.map(x => [x[0], 100]),
                    stroke: '#f36581'
                }
            ],
            yScale: 'linear',
            height: 300,
            xGrid: false,
            yGrid: false,
        });
    }


    const handleChartPoints = (newDates) => {
        // Sort the selected dates
        const sortedDates = newDates.sort((a, b) => a.localeCompare(b));

        // Function to find the data point for each selected date in a time series
        const getDataForDate = (timeSeries, date) => {
            const dataPoint = timeSeries.find(item => item.timestamp === date);
            // If a matching data point is found, return it, otherwise return the nearest point (or null if none exists)
            return dataPoint ? dataPoint : getNearestDataPoint(timeSeries, date);
        };

        // Function to find the nearest data point if exact date is not found
        const getNearestDataPoint = (timeSeries, date) => {
            const timeStamps = timeSeries.map(item => item.timestamp);
            const closestTimestamp = timeStamps.reduce((prev, curr) =>
                Math.abs(new Date(curr) - new Date(date)) < Math.abs(new Date(prev) - new Date(date)) ? curr : prev
            );
            return timeSeries.find(item => item.timestamp === closestTimestamp);
        };
        setSelectedDates(sortedDates);
        // Get the selected points from portfolio, benchmark, and extra time series
        const selectedPoints = sortedDates.map(date => {
            const portfolioData = getDataForDate(portfolioTimeSeries, date);
            const benchmarkData = getDataForDate(benchmarkTimeSeries, date);
            const extraSeries = [];
            if (extraTimeseries && extraTimeseries.length > 0) {
                extraTimeseries.forEach(x=>{
                    const { name, data } = x;
                    const point = getDataForDate(data, date);
                    extraSeries.push({name, data: [point?.timestamp, point?.['adj close']]});
                })
            }
            return {
                timestamp: date,
                items: [
                    {name: portfolio.name, data: [portfolioData?.timestamp, portfolioData?.['adj close'], portfolioData?.snapshot]},
                    {name: 'Benchmark', data: [benchmarkData?.timestamp, benchmarkData?.['adj close']]},
                    ...extraSeries
                ]
            };
        });
        submitGptPrompt(selectedPoints);

    };

    const plotlyTimeseriesData = portfolioTimeSeries && benchmarkTimeSeries && [
        { name: 'Portfolio', data: portfolioTimeSeries.map(x=>[x.timestamp, x['adj close']]) },
        { name: 'Benchmark', data: benchmarkTimeSeries.map(x=>[x.timestamp, x['adj close']]), color: '#8292a2' }
    ];

    const selectedMetrics = selectedDates.length > 0 && compositeMetric?.length > 0 && (
        compositeMetric.filter(x=>selectedDates.includes(x[0])).map(x=>({date: x[0], beta: x[1]})
    ));




    return promptVisible === false || !gptResponse ? (
        <>
            <Row  gutter={[32, 0]} >
                <Col flex={'0 0 300px'} className={'dropped-shadow-hover'} style={{borderRight: '1px solid #ededed'}}>
                    <Snapshot snapshot={snapshotData} categoryMapping={categoryMapping} colorLookup={tickerLookup} date={snapshotDate}/>
                </Col>
                <Col flex={'1 1'} style={{padding: '15px 50px 5px 15px'}}>
                    <StockChart
                        height={'70vh'}
                        ref={chartRef}
                        dataSeries={chartDataSeriesConfig}
                        customSnapshotCallback={snapshotCallback}
                        extraToolsLeft={
                        <Space>
                            <ButtonGroup>
                                <Button startIcon={loadingResponse ? <Spinner text={"Loading..."} /> : <PlaylistAdd/>}
                                        size={'small'}
                                        variant={'outlined'}
                                        color={'secondary'}
                                        onClick={()=>setShowChartSelector(true)}
                                >
                                    Talking Points
                                </Button>
                                {!showPortfolioCompositeMetrics ? (
                                    <Button startIcon={loadingComposite ?  <Spinner text={"Loading..."}/>  : <PlaylistAdd/>}
                                            size={'small'}
                                            variant={'outlined'}
                                            color={'secondary'}
                                            onClick={handleFetchCompositeMetric}
                                            disabled={relativeMetrics.isLoading}
                                    >
                                        Metrics
                                    </Button>
                                ) : <CloseButton text={'Hide Metrics'} toggle={setShowPortfolioCompositeMetrics} />}
                                <TimeseriesMenu
                                    onPlotHoldings={onPlotHoldings}
                                    allTickers={allTickers}
                                    selectedTickers={selectedTickers}
                                    setSelectedTickers={setSelectedTickers}
                                    currentTimeseries={Array.from(new Set([...portfolioTimeSeries, ...benchmarkTimeSeries, ...extraTimeseries].map(series => series.symbol))).length}
                                />
                                {gptResponse && (
                                    <Tooltip title={'View Talking Points for This Portfolio'}>
                                        <Button size={'small'} onClick={()=>setPromptVisible(true)} variant={'contained'} color={'info'}>
                                            <CoPresentRounded fontSize={'small'}/>
                                        </Button>
                                    </Tooltip>
                                )}
                                <Tooltip title={'Toggle Visibility of the Returns Table'}>
                                    <Button size={'small'} variant={showReturnsTable===true?'contained':'outlined'} color={'secondary'} onClick={()=>setShowReturnsTable(!showReturnsTable)}>
                                        {showReturnsTable ? 'Hide Table' : 'Show Table'}
                                    </Button>
                                </Tooltip>
                                <Tooltip title={'Add/Remove the Change Series Secondary Chart Below'}>
                                    <Button color={'secondary'} size={'small'} variant={showPortfolioTurnover ? 'contained' : 'outlined'} onClick={handleDeltaSeries}>
                                        <WaterfallChartRounded fontSize={'small'} />
                                    </Button>
                                </Tooltip>
                            </ButtonGroup>
                        </Space>
                        }
                    />

                    {!showPortfolioCompositeMetrics && showReturnsTable===true && (
                        <div style={{padding: '20px'}}>
                            <YearlyReturnsTable
                                seriesData={[
                                    { name: portfolio?.name, data: portfolioTimeSeries },
                                    { name: 'Benchmark', data: benchmarkTimeSeries }
                                ]}
                            />
                        </div>
                    )}
                </Col>
            </Row>

            <Modal open={showChartSelector}
                   onCancel={()=>setShowChartSelector(false)}
                   onOk={()=>{
                       handleChartPoints()
                       setShowChartSelector(false)
                   }}
                   width={1200}
                   footer={null}
            >
                {portfolioTimeSeries && benchmarkTimeSeries && (
                    <ChartSelector
                          timeseriesData={plotlyTimeseriesData}
                          onSubmit={handleChartPoints}
                      />
                )}
            </Modal>

        </>

        ) : <BlotterResponse response={gptResponse} 
                             data={promptRows} 
                             closeResponse={()=>setPromptVisible(false)}
                             extraDetails={selectedMetrics}
                             timeSeriesData={formatTimeseriesForBlotterView({timeseriesData: plotlyTimeseriesData, selectedDates})}
    />
};



export default PortfolioChart;